import { AdminPageHeader, AdminPageShell } from '@/components/patterns/admin-page'
import { SeasonalDesignEditor } from '@/components/seasonal/SeasonalDesignEditor'
import { Badge } from '@/components/ui/badge'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { designQueries } from '@/lib/services/designs/designs.query'
import {
  merchandisingTagQueries,
  useMerchandisingTagGroups,
} from '@/lib/services/merchandising-tags/merchandising-tags.query'
import { flattenSeasonalContentTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import {
  seasonalContentQueries,
  useSeasonalDesign,
} from '@/lib/services/seasonal-content/seasonal-content.query'
import { requireGlobalSeasonalContentAccess } from '@/lib/services/seasonal-content/seasonal-content.route'
import { seasonalDesignParamsSchema } from '@/lib/services/seasonal-content/seasonal-content.schema'
import { storeQueries } from '@/lib/services/stores/stores.query'
import { createFileRoute } from '@tanstack/react-router'
import { Images } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/seasonal-content/$seasonalDesignId')({
  component: EditSeasonalDesignPage,
  head: adminRouteTitleHead,
  params: { parse: (params) => seasonalDesignParamsSchema.parse(params) },
  loader: async ({ context, params }) => {
    const access = requireGlobalSeasonalContentAccess({ context, params })

    await Promise.all([
      context.queryClient.ensureQueryData(
        seasonalContentQueries.design({
          organizationId: params.orgId,
          designId: params.seasonalDesignId,
        })
      ),
      context.queryClient.ensureQueryData(
        merchandisingTagQueries.index({ organizationId: params.orgId })
      ),
      context.queryClient.ensureQueryData(designQueries.default({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)),
      context.queryClient.ensureQueryData(storeQueries.themes({ organizationId: params.orgId })),
    ])

    return {
      ...access,
      canCreateStockImages: context.ability.can('create', 'stock-images'),
    }
  },
})

export function EditSeasonalDesignPage() {
  const { orgId, seasonalDesignId } = Route.useParams()
  const { canCreateStockImages, isReadOnly } = Route.useLoaderData()
  const { data: response } = useSeasonalDesign({
    organizationId: orgId,
    designId: seasonalDesignId,
  })
  const { data: merchandisingTagGroups = [] } = useMerchandisingTagGroups({ organizationId: orgId })
  const seasonalTags = flattenSeasonalContentTagGroups(merchandisingTagGroups)
  const design = response?.data ?? response

  return (
    <AdminPageShell className="pb-0 xl:grid xl:h-[calc(100dvh-5.5rem)] xl:min-h-0 xl:grid-rows-[auto_minmax(0,1fr)] xl:gap-4 xl:space-y-0 xl:overflow-hidden">
      <AdminPageHeader
        icon={Images}
        title={design.name}
        description="Draft changes stay private until you publish. Publishing updates every florist website still referencing this design."
        badges={
          <>
            <Badge variant="outline">{design.usage_count} using it</Badge>
            <Badge variant="outline">{design.live_impact_count} live now</Badge>
          </>
        }
      />
      <SeasonalDesignEditor
        key={design.updated_at}
        design={design}
        seasonalTags={seasonalTags}
        canCreateStockImages={canCreateStockImages}
        isReadOnly={isReadOnly}
        organizationId={orgId}
      />
    </AdminPageShell>
  )
}
